import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getEntityKindFromEntityType } from '@/modules/entities/utils/getEntityKindFromEntityType';
import { DirectHoldingsGroupSection } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/DirectHoldingsGroupSection';
import { TrustsAndBusinesses } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/TrustsAndBusinesses/TrustsAndBusinesses';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';

import { useEstateHoldingsContext } from '../../../contexts/estateHoldings.context';
import { DirectHoldingsGroupCardVariant } from '../../EstateDirectHoldings.types';
import {
  filterHoldingsGroupsByClient,
  filterTrustsAndBusinessesByClient,
} from '../../EstateDirectHoldings.utils';

export interface HoldingsGroupCardProps {
  variant: DirectHoldingsGroupCardVariant;
  clientProfile?: ContextPrimaryClient;
  onEditEntitySection?: (entityId: string, section: EditEntitySection) => void;
  primaryClients?: ContextPrimaryClient[];
  suggestionsOnly?: boolean;
}

interface HoldingsGroupCardInnerProps {
  variant: DirectHoldingsGroupCardVariant;
  clientProfile?: ContextPrimaryClient;
  holdingsGroups: EstatePage_EntityFragment[];
  onEditEntitySection?: (entityId: string, section: EditEntitySection) => void;
  suggestionsOnly: boolean;
}

export function DirectHoldingsGroupCard({
  variant,
  clientProfile,
  onEditEntitySection,
  primaryClients,
  suggestionsOnly = false,
}: HoldingsGroupCardProps) {
  const { entities } = useEstateHoldingsContext();
  const primaryClientIds = useMemo(
    () => primaryClients?.map((client) => client.id),
    [primaryClients]
  );
  const holdingsGroups = useMemo(
    () =>
      filterHoldingsGroupsByClient(
        entities ?? [],
        variant,
        clientProfile?.id,
        primaryClientIds
      ),
    [entities, variant, clientProfile?.id, primaryClientIds]
  );

  return (
    <HoldingsGroupCardInner
      variant={variant}
      clientProfile={clientProfile}
      holdingsGroups={holdingsGroups}
      onEditEntitySection={onEditEntitySection}
      suggestionsOnly={suggestionsOnly}
    />
  );
}

function HoldingsGroupCardInner({
  variant,
  clientProfile,
  holdingsGroups,
  onEditEntitySection,
  suggestionsOnly,
}: HoldingsGroupCardInnerProps) {
  const { entities } = useEstateHoldingsContext();

  const trustsAndBusinesses = useMemo(
    () => filterTrustsAndBusinessesByClient(entities ?? [], clientProfile?.id),
    [entities, clientProfile?.id]
  );

  const filteredHoldingsGroups = holdingsGroups.filter(
    (holdingsGroup) =>
      holdingsGroup.kind === getEntityKindFromEntityType(variant)
  );

  const showTrustsAndBusinesses =
    variant == 'individual-account' &&
    clientProfile &&
    trustsAndBusinesses.length > 0;

  // Return null if there's nothing to render to avoid empty spacing
  if (!showTrustsAndBusinesses && filteredHoldingsGroups.length === 0) {
    return null;
  }

  const holdingsGroupSections = filteredHoldingsGroups.map((holdingsGroup) => (
    <DirectHoldingsGroupSection
      variant={variant}
      key={`holdings-group-section-${holdingsGroup.id}`}
      holdingsGroup={holdingsGroup}
      onEditEntitySection={onEditEntitySection}
      suggestionsOnly={suggestionsOnly}
    />
  ));

  // Only use Stack wrapper when we need spacing between multiple items
  if (!showTrustsAndBusinesses) {
    return <>{holdingsGroupSections}</>;
  }

  return (
    <Stack spacing={1}>
      <TrustsAndBusinesses
        clientProfile={clientProfile}
        holdingsGroups={trustsAndBusinesses}
      />
      {holdingsGroupSections}
    </Stack>
  );
}
